import { useGridFilters } from "@repo/baseCloud";
import { SegmentFilters } from "@repo/baseCloud/components";
import { DateTimeRangePicker, DropDownList, FiltersV2, Loading, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type { PreEnrollmentValidationFilter } from "./types";

import PreEnrollmentValidationApi from "../../api/preEnrollmentValidationApi";
import { PreEnrollmentValidationGrid } from "./grid";
import * as STRINGS from "./strings";
import style from "./style.module.scss";

const initialFilters: PreEnrollmentValidationFilter = {
	currentPage: 1,
	itemsPerPage: 20,
	startDate: null,
	endDate: null,
	validationTypeIds: [],
	branchIds: [],
};

export function PreEnrollmentValidationPage() {
	const {
		filters: draftFilters,
		setFilters: setDraftFilters,
		applyNow,
		appliedFilters,
		setAppliedFilters,
		filtersProps,
	} = useGridFilters(initialFilters);

	const validationTypesQuery = useQuery({
		queryKey: ["EnrollmentValidationTypes"],
		queryFn: () => PreEnrollmentValidationApi.getTypes(),
	});

	const validationTypeOptions = useMemo(
		() => validationTypesQuery.data?.map((t) => ({ label: t.Name, value: String(t.Id) })) ?? [],
		[validationTypesQuery.data],
	);

	const filterChanged = (value: any, name: string) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		<Loading loading={false}>
			<main>
				<FiltersV2 {...filtersProps}>
					<DateTimeRangePicker
						dateOnly
						id="date-range"
						label="Date of Error Range"
						className={style.dateTimeRangePicker}
						quickRange
						quickRangeOptions={["today", "yesterday", "quarter", "year", "all"]}
						value={[draftFilters.startDate, draftFilters.endDate]}
						setValue={(v: (Date | undefined)[]) =>
							setDraftFilters({ ...draftFilters, startDate: v[0], endDate: v[1] })
						}
						onQuickRangeSelected={(v) => applyNow((prev) => ({ ...prev, startDate: v[0], endDate: v[1] }))}
					/>

					<DropDownList
						id={STRINGS.VALIDATION_TYPE_IDS}
						label="Validation Type"
						value={draftFilters.validationTypeIds?.[0]?.toString() || ""}
						// Single-select, but the filter holds an array of ids.
						changeHandler={(v) => filterChanged(v ? [parseInt(v)] : [], STRINGS.VALIDATION_TYPE_IDS)}
						fullLength
						clearable
						loaded={validationTypesQuery.isFetched}
						values={validationTypeOptions}
					/>

					<SegmentFilters onChange={segmentFiltersChanged} />
					<TextBox
						id={STRINGS.AGENT_ID}
						label="Agent Id"
						value={draftFilters.agentId ?? ""}
						changeHandler={filterChanged}
					/>
					<div className="flex gap-1 [&>div]:m-0">
						<TextBox
							id={STRINGS.ENROLLMENT_ID}
							label="Enrollment Id"
							value={draftFilters.enrollmentId ?? ""}
							changeHandler={filterChanged}
						/>
						<TextBox
							id={STRINGS.SALE_ID}
							label="Sale Id"
							value={draftFilters.saleId ?? ""}
							changeHandler={filterChanged}
						/>
					</div>
					<TextBox
						id={STRINGS.ENTITY_VALUE}
						label="Entity Value"
						value={draftFilters.entityValue ?? ""}
						changeHandler={filterChanged}
					/>
				</FiltersV2>
				<PreEnrollmentValidationGrid filters={appliedFilters} setFilters={setAppliedFilters} isAdmin={true} />
			</main>
		</Loading>
	);
}
